import { actions, IdType } from 'react-table';

import { StateReducer } from './createStateReducer';

const TOGGLE_SELECTED_ROWS_ACTIONS = Object.freeze([
  actions.toggleAllRowsSelected,
  actions.toggleAllPageRowsSelected,
]);

export function disallowDisabledRowSelection<
  D extends Record<string, unknown>
>() {
  return ({
    state,
    previousState,
    action,
    instance,
    ...rest
  }: StateReducer<D>) => {
    const isSelectingAllRowsAsOpposedToDeselectingAllRows =
      action.value === true;
    if (
      TOGGLE_SELECTED_ROWS_ACTIONS.includes(action.type) &&
      isSelectingAllRowsAsOpposedToDeselectingAllRows &&
      instance
    ) {
      const selectedRowIds = { ...state.selectedRowIds };
      Object.keys(state?.rowState || {}).forEach((rowId) => {
        const rowState: { isDisabled?: boolean } | undefined =
          state?.rowState?.[rowId];
        // if the currently disabled row is not selected prior to this
        // action we want to omit it from selection
        if (rowState?.isDisabled && !previousState.selectedRowIds[rowId]) {
          delete selectedRowIds[rowId];
        }
      });

      return {
        ...rest,
        state: { ...state, selectedRowIds },
        action,
        previousState: state,
        instance,
      };
    }

    if (
      TOGGLE_SELECTED_ROWS_ACTIONS.includes(action.type) &&
      !isSelectingAllRowsAsOpposedToDeselectingAllRows &&
      instance
    ) {
      const modifiedSelectedRowIds = Object.keys(state.rowState || {}).reduce<
        Record<IdType<D>, boolean>
      >((selectedRowIds, rowId) => {
        const rowState: { isDisabled?: boolean } | undefined =
          state.rowState?.[rowId];
        return {
          ...selectedRowIds,
          // if this row is disabled and it was previously selected, continue to select
          // it, otherwise it was previously unselected and can remain unselected
          ...(rowState?.isDisabled && previousState.selectedRowIds[rowId]
            ? { [rowId]: true }
            : {}),
        };
      }, {} as Record<string, never>) as Record<IdType<D>, boolean>;
      return {
        ...rest,
        state: { ...state, selectedRowIds: modifiedSelectedRowIds },
        action,
        previousState: state,
        instance,
      };
    }

    return { state, action, instance, previousState, ...rest };
  };
}
